import { seoLinks, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";
import { GuideLinks } from "../components/site/Guides";
import { KnowledgeHubNote } from "../components/site/KnowledgeHubNote";
import { NewcomerNote } from "../components/site/NewcomerNote";

const title = "Flytta till Spanien med barn – guide för svenska familjer";
const description =
  "Praktisk guide för familjer som flyttar till Costa del Sol: tidsplan, skolval, boende och vad som är bra att ordna före och efter flytten.";

export const Route = createFileRoute("/flytta-till-spanien-med-barn")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/flytta-till-spanien-med-barn"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guide"
        title="Flytta till Spanien med barn"
        intro="En flytt till Costa del Sol är ofta lika mycket ett familjeprojekt som ett praktiskt. Här är en översikt över vad som brukar behöva planeras, i vilken ordning – och hur skolfrågan hänger ihop med resten."
      />

      <NewcomerNote />

      <Prose>
        <h2>Börja med läsåret, inte med flyttlasset</h2>
        <p>
          Det som styr tidsplanen mest för en barnfamilj är skolans läsår. Terminerna avgör när
          barnen naturligt kan gå in i en klass, när eventuella distansstudier startar och när det
          finns plats i grupperna. Många familjer utgår därför från önskad skolstart och räknar
          bakåt: boende och praktiska ärenden får anpassa sig efter terminen, inte tvärtom.
        </p>
        <p>
          Samtidigt behöver flytten inte vänta på ett terminsskifte. Hos oss tas ansökningar emot
          löpande och det går bra att börja mitt i terminen när det finns plats. För familjer som
          flyttar av jobbskäl mitt under året är det ofta den avgörande skillnaden – barnen kan
          komma in i en trygg skolvardag direkt i stället för att vänta i flera månader.
        </p>

        <h2>Ungefärlig tidsplan</h2>
        <ul>
          <li>
            <strong>6–12 månader före:</strong> undersök områden och skolor, gör studiebesök om ni
            har möjlighet, prata igenom flytten med barnen och kartlägg arbets- och boendesituation.
          </li>
          <li>
            <strong>3–6 månader före:</strong> ta kontakt med skolan, ställ frågor om årskurs,
            språknivå och eventuella behov av extra stöd, och gör en ansökan.
          </li>
          <li>
            <strong>1–3 månader före:</strong> ordna boende, se över försäkringar, banktjänster och
            hur ni ska hantera folkbokföring och identitetsnummer i Spanien.
          </li>
          <li>
            <strong>Efter flytten:</strong> registrera er lokalt, se över sjukvårdstillhörighet och
            skapa rutiner för skolskjuts, fritidsaktiviteter och vardagslogistik.
          </li>
        </ul>
        <p>
          Tidsplanen är en riktlinje, inte en regel. Familjer som redan har varit i området eller
          har ett jobb på plats klarar sig ofta med betydligt kortare framförhållning.
        </p>

        <h2>Skolvalet – de tre vanliga vägarna</h2>
        <p>
          Svenska familjer på Costa del Sol brukar väga tre alternativ mot varandra. En spansk skola
          ger snabb språkinlärning och lokal förankring, men innebär att undervisningen sker helt på
          spanska och följer den spanska läroplanen. En internationell skola undervisar oftast på
          engelska och passar familjer som räknar med att flytta vidare till ett annat land. En
          svensk skola följer den svenska läroplanen, vilket gör återgången till Sverige enkel och
          gör att barnen behåller sitt svenska språk och sina ämneskunskaper.
        </p>
        <p>
          Många familjer vet inte hur länge de kommer att stanna. Just den osäkerheten är ofta skäl
          nog att välja en skolgång som fungerar både om ni blir kvar och om ni flyttar hem: svensk
          läroplan i botten, med spanska och engelska varje dag så att barnen ändå kommer in i det
          lokala livet.
        </p>
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale="sv" />
      </div>

      <Prose>

        <h2>Språket är en process, inte ett hinder</h2>
        <p>
          Barn lär sig språk i olika takt. Räkna med att det tar tid innan spanskan sitter i
          vardagssituationer, och att det första halvåret ofta handlar mer om trygghet än om
          resultat. Ett tips är att skilja på vardagsspråk och skolspråk: barn kan låta flytande på
          lekplatsen långt innan de klarar ämnesundervisning på samma språk. Därför är det värdefullt
          att kunskapsutvecklingen sker på ett språk barnet redan behärskar, medan spanskan byggs upp
          parallellt.
        </p>

        <h2>Boende och vardagslogistik</h2>
        <p>
          Området mellan Estepona och Marbella är utsträckt och det mesta sker med bil. Innan ni
          bestämmer boende är det klokt att räkna på pendlingen till skolan i rusningstid, inte bara
          på kartan. Många familjer hyr först i sex till tolv månader för att lära känna området
          innan de köper eller binder upp sig längre.
        </p>
        <p>
          Fundera också på eftermiddagarna. Fritidsverksamhet, träning och aktiviteter påverkar hur
          dagen ser ut mer än man tror, särskilt om båda vuxna arbetar. Hos oss finns fritids fram
          till 16.30 och möjlighet att kombinera skolan med idrottsprofiler i fotboll, padel och
          tennis.
        </p>

        <h2>Frågor att ställa till varje skola ni tittar på</h2>
        <ul>
          <li>Vilken läroplan följs, och vad innebär den om vi flyttar tillbaka till Sverige?</li>
          <li>Hur stora är grupperna och hur många vuxna finns runt barnen?</li>
          <li>Hur mycket språkundervisning får eleverna, och på vilken nivå?</li>
          <li>Hur arbetar skolan med elever som behöver extra stöd?</li>
          <li>Går det att börja mitt i terminen?</li>
          <li>Vad ingår i avgifterna och vad tillkommer?</li>
        </ul>

        <h2>Nästa steg</h2>
        <p>
          Vill ni veta hur svensk skolgång fungerar utomlands rent formellt kan ni läsa vidare i vår
          guide om{" "}
          <Link className="text-primary link-quiet" to="/svensk-skola-i-spanien">
            svensk skola i Spanien
          </Link>
          . Är ni redan på väg är det enklaste att göra en ansökan – den är inte bindande, och vi
          återkommer med besked om plats och vilka avgifter som gäller.
        </p>
      </Prose>

      <KnowledgeHubNote resource="flyttchecklistan" />

      <GuideLinks current="/flytta-till-spanien-med-barn" />
      <CtaBand />
      <div className="h-20" />
    </>
  );
}
